import { Component, Input } from '@angular/core';

/**
 * 🆕 Stats compactos del estado de un conjunto de cuotas.
 * Acepta cualquier array de PaymentPlan/CorporatePaymentPlan-like.
 * Cuenta por: PAID, PARTIALLY_PAID, PENDING, LINK_GENERATED, OVERDUE, castigadas.
 *
 * Uso:
 *   <app-installment-stats [plans]="enrollment.paymentPlans"></app-installment-stats>
 */
@Component({
  selector: 'app-installment-stats',
  templateUrl: './installment-stats.component.html'
})
export class InstallmentStatsComponent {
  @Input() plans: any[] | null | undefined = [];
  /** Si true, muestra también las cuotas castigadas como chip. */
  @Input() showPunished: boolean = true;
  /** Estilo: 'card' = bloque destacado, 'inline' = pills delgadas inline. */
  @Input() variant: 'card' | 'inline' = 'card';
  /** Título opcional del bloque (solo en variant=card). */
  @Input() title: string = 'Estado de cuotas';

  get total(): number {
    return this.plans?.length ?? 0;
  }

  /**
   * Una cuota se cuenta como "vencida" cuando NO está PAID, no está castigada,
   * y su DueDate ya pasó. Es derivada (no es un PaymentStatus enum), por eso
   * la calculamos aquí.
   */
  get overdueCount(): number {
    if (!this.plans?.length) return 0;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return this.plans.filter(p => {
      if (!p) return false;
      if (p.isPunished) return false;
      if (this.matchStatus(p, 'PAID')) return false;
      const due = p.dueDate ? new Date(p.dueDate) : null;
      return !!due && due < today;
    }).length;
  }

  get paidCount(): number { return this.countByStatus('PAID'); }
  get partialCount(): number { return this.countByStatus('PARTIALLY_PAID'); }
  get pendingCount(): number { return this.countByStatus('PENDING'); }
  get linkGeneratedCount(): number { return this.countByStatus('LINK_GENERATED'); }
  get punishedCount(): number {
    return this.plans?.filter(p => p?.isPunished).length ?? 0;
  }

  /** Trabaja con strings o ints del enum PaymentStatus. */
  private matchStatus(plan: any, target: string): boolean {
    if (!plan) return false;
    const status = plan.paymentStatus;
    if (status == null) return false;
    if (typeof status === 'string') return status.toUpperCase() === target;
    // Mapeo de enum int → label (mismo que usa el backend)
    const intToLabel: Record<number, string> = {
      0: 'PENDING',
      1: 'PAID',
      2: 'LINK_GENERATED',
      3: 'OVERDUE',
      4: 'PARTIALLY_PAID'
    };
    return intToLabel[status as number] === target;
  }

  private countByStatus(target: string): number {
    if (!this.plans?.length) return 0;
    // Las castigadas se cuentan aparte, no las mezclamos en otros estados
    return this.plans.filter(p => !p?.isPunished && this.matchStatus(p, target)).length;
  }
}
